// @angular
import {
  Component,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  effect,
  inject,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { Router, RouterLink } from "@angular/router";
// Translate
import { TranslateService, TranslateModule } from "@ngx-translate/core";
// Own services
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { trustImageDataUrl } from "../../../services/shared/html-sanitizer.util";
import {
  LanguageControllerService,
  LANGUAGE
} from "../../../services/language/LanguageController.service";
import { UserControllerService } from "../../../services/server/UserController.service";
import { EntityControllerService } from "../../../services/server/EntityController.service";
import { NotificationModalService } from "../../../modules/notification-module/notification-service/notification-modal.service";
import { NotificationControllerService } from "../../../services/server/NotificationController.service";
import { DateParserService } from "../../../services/shared/DateParserService.service";
// Variables
import { NgClass } from "@angular/common";
import { DomSanitizer } from "@angular/platform-browser";
import { DataProtectedPipe } from "../../../pipes/data-protected.pipe";

/**
 * Navbar for subscribers: CUPS dropdown with search, entity logo, language selector, logout (logoutFlag)
 * and pending notification counter, refreshed every 5 minutes.
 * Loads the CUPS list with UserController.getSubscriberCupsDropdown and shares it via session data.
 */
@Component({
  selector: "app-subscriber-nav",
  templateUrl: "./subscriber-nav.component.html",
  styleUrls: ["./subscriber-nav.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, NgClass, TranslateModule, DataProtectedPipe]
})
export class SubscriberNavComponent implements OnInit, OnDestroy {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  private readonly domSanitizer = inject(DomSanitizer);

  // Session variables
  sessionUser: string;
  sessionLanguage: string;
  subscriberCupsList: any[];

  // Language
  languageName: string;

  // Logout event, handled by the parent
  @Output() logoutFlag = new EventEmitter<any>();

  // Logo
  logoImg: any;
  logoImgUrl: any;
  logoLoading: boolean = true;
  logoId: number;

  // Search box
  searchActive: boolean = false;
  searchPlaceholder: string = this.translate.instant("search");
  subscriberList: any[] = [];
  subscriberListFiltered: any[] = [];
  selection: string;

  // Body
  body: any = document.body;

  // CUPS
  cupsData: any;
  cupsId: number;
  pendingNotifications: number = 0;
  notificationsRequestTimerMS: number = 300000;
  notificationsRequestTimer: any;

  // Languages
  languageList = [
    {
      id: LANGUAGE.ESPANOL,
      title: "Castellano",
      img: "assets/img/languages/spain-flag.png"
    },
    {
      id: LANGUAGE.CATALAN,
      title: "Catalá",
      img: "assets/img/languages/cat-flag.png"
    },
    {
      id: LANGUAGE.INGLES,
      title: "English",
      img: "assets/img/languages/uk-flag.png"
    },
    {
      id: LANGUAGE.FRANCES,
      title: "Français",
      img: "assets/img/languages/france-flag.png"
    },
    {
      id: LANGUAGE.ITALIANO,
      title: "Italiano",
      img: "assets/img/languages/it-flag.png"
    },
    {
      id: LANGUAGE.PORTUGUES,
      title: "Português",
      img: "assets/img/languages/pt-flag.png"
    }
  ];

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly DateParserService: DateParserService,
    private readonly EntityController: EntityControllerService,
    readonly LanguageController: LanguageControllerService,
    private readonly NotificationController: NotificationControllerService,
    public notificationModal: NotificationModalService,
    private readonly router: Router,
    private readonly SessionDataService: SessionDataService,
    private readonly UserController: UserControllerService,
    private readonly translate: TranslateService
  ) {
    effect(() => {
      const cupsId = this.SessionDataService.subscriberCups();
      if (cupsId !== null && this.cupsId !== cupsId) {
        this.cupsId = cupsId;
        this.getCupsData(cupsId);
        if (this.subscriberCupsList) {
          this.getEntityImg(
            this.subscriberCupsList?.find((cups: any) => cups.id === cupsId)?.entityId
          );
        }
      }
    });

    effect(() => {
      const language = this.SessionDataService.language();
      if (language) {
        this.sessionLanguage = language;
        this.languageSelector();
        this.updateLanguageList(language === "CA" ? LANGUAGE.CATALAN : language);
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/
  ngOnInit(): void {
    // Load initial values
    this.sessionUser = untracked(() => this.SessionDataService.user());
    this.sessionLanguage = untracked(() => this.SessionDataService.language());
    this.languageSelector();
    this.updateLanguageList(
      this.sessionLanguage === "CA" ? LANGUAGE.CATALAN : this.sessionLanguage
    );

    this.loadComponent();
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {
    clearInterval(this.notificationsRequestTimer);
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Load component
  loadComponent(): void {
    this.getSubscriberData();
    this.getPendingNotifications();
    clearInterval(this.notificationsRequestTimer);
    this.notificationsRequestTimer = setInterval(
      () => this.getPendingNotifications(),
      this.notificationsRequestTimerMS
    );
  }

  // Get the entity logo
  getEntityImg(logoId: number): void {
    if (logoId && logoId !== this.logoId) {
      this.logoId = logoId;
      this.EntityController.getImg(this.logoId).subscribe(response => {
        if (response["code"] === 0) {
          this.logoImg = response["body"];
          if (this.logoImg) {
            this.logoImgUrl =
              trustImageDataUrl(this.domSanitizer, this.logoImg) ?? "assets/img/logos/arsondata.png";
          } else {
            this.logoImgUrl = "assets/img/logos/arsondata.png";
          }
        }
      });
    }
  }

  // Load the subscriber CUPS dropdown, share it via session data and refresh the entity logo
  getSubscriberData(): void {
    this.UserController.getSubscriberCupsDropdown().subscribe(response => {
      if (response["code"] === 0 && response["body"]) {
        this.subscriberCupsList = response["body"];
      } else {
        this.subscriberCupsList = [];
      }
      if (this.cupsId) {
        this.getEntityImg(
          this.subscriberCupsList?.find((cups: any) => cups.id === this.cupsId)?.entityId
        );
      } else {
        this.getEntityImg(this.subscriberCupsList[0]?.entityId);
      }
      this.SessionDataService.sendSubscriberCupsList(this.subscriberCupsList);
    });
  }

  // Load CUPS details, parse meter read timestamps and share them via session data
  getCupsData(currentCupsId: number): void {
    this.UserController.getSubscriberMain(currentCupsId).subscribe(response => {
      if (response["code"] === 0 && response["body"]) {
        const cupsData: any = response["body"];
        const timeZone = cupsData?.timeZone || "Europe/Madrid";

        if (cupsData.contadores) {
          cupsData.contadores.forEach((meter: any) => {
            if (meter?.lastReadedTimestamp != null) {
              meter.lastReadedTimestampParsed = this.DateParserService.parseDate(
                meter.lastReadedTimestamp,
                "L",
                timeZone
              );
            } else {
              meter.lastReadedTimestampParsed = "-";
            }
          });
        } else {
          cupsData.contadores = [];
        }

        this.cupsData = cupsData;
      }

      this.SessionDataService.sendSubscriberCupsData(this.cupsData);
    });
  }

  // Set languageName from the session language
  languageSelector(): void {
    switch (this.sessionLanguage) {
      case LANGUAGE.INGLES:
        this.languageName = "English";
        break;
      case LANGUAGE.ESPANOL:
        this.languageName = "Castellano";
        break;
      case "ca":
        this.languageName = "Català";
        break;
      case LANGUAGE.CATALAN:
        this.languageName = "Català";
        break;
      case LANGUAGE.FRANCES:
        this.languageName = "Français";
        break;
      case LANGUAGE.PORTUGUES:
        this.languageName = "Português";
        break;
      default:
        this.languageName = "English";
        break;
    }
  }

  // Change user language
  switchLanguage(newLanguage: string): void {
    this.SessionDataService.sendLanguage(newLanguage);
  }

  // Update the language list order
  updateLanguageList(newLanguage: string): void {
    let currentLanguage: any[] = this.languageList.splice(
      this.languageList.findIndex((language: any) => language.id === newLanguage),
      1
    );
    this.languageList.unshift(currentLanguage[0]);
  }

  // Emit logout to the parent
  logout(): void {
    this.logoutFlag.emit();
  }

  // Go to subscriber settings
  goToConfig(): void {
    // Prevents scrollbars in the subscriber interface
    this.body.style.overflow = "hidden";
    this.router.navigate(["/abonado/configuracion"]);
  }

  // Get the number of pending notifications
  getPendingNotifications() {
    this.NotificationController.getUserPendingNotifications().subscribe(response => {
      if (response["code"] === 0) {
        this.pendingNotifications = response["body"];
      }
    });
  }
}
